import { Component, OnInit, ViewChild, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PasswordReqDialogComponent } from './password-req-dialog.component';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginService } from 'src/app/core/services/login.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { NativeBridgeService } from 'src/app/core/services/native-bridge.service';
import { deviceMessageId } from 'src/app/core/enums/device-message-id.enum';

@Component({
  selector: 'app-recovery-password',
  templateUrl: './recovery-password.component.html',
  styleUrls: ['./recovery-password.component.scss']
})
export class RecoveryPasswordComponent implements OnInit {
  email = '';
  passwordType = 'password';
  showPasswordPanel = false;
  overlayPositions = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 0
    }
  ];
  scrollStrategy: any;

  private PASSWORD_REGEXP = new RegExp(
    /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-={}|\[\]:";'<>?,./`~])(?!.*(.)\1\1)[\s\S]{12,50}$/
  );

  togglePassword() {
    this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
  }
  @ViewChild('passwordTooltip', { read: MatTooltip }) passwordTooltip!: MatTooltip;

  dialogRef: MatDialogRef<PasswordReqDialogComponent> | null = null;

  openPasswordDialog() {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const input = document.getElementById('passwordInput');
        if (!input) return;
        const rect = input.getBoundingClientRect();
        if (this.dialogRef) return;
        this.dialogRef = this.dialog.open(PasswordReqDialogComponent, {
          data: { passwordReqs: { ...this.passwordReqs } },
          hasBackdrop: false,
          panelClass: 'password-dialog-panel',
          autoFocus: false,
          restoreFocus: false,
          position: { top: `${rect.top}px`, left: `${rect.right + 32}px` }
        });
        this.dialogRef.backdropClick().subscribe(() => this.closePasswordDialog());
        // Listen for escape key and blur
        input.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('scroll', this.repositionDialog, true);
        window.addEventListener('resize', this.repositionDialog, true);
        window.addEventListener('mousedown', this.handleGlobalClick, true);
        // Always refresh requirements when opening
        if (this.dialogRef && this.dialogRef.componentInstance.updateReqs) {
          this.dialogRef.componentInstance.updateReqs({ ...this.passwordReqs });
        }
      }, 0);
    });
  }

  closePasswordDialog = () => {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
      const input = document.getElementById('passwordInput');
      if (input) {
        input.removeEventListener('keydown', this.handleKeyDown);
      }
      window.removeEventListener('scroll', this.repositionDialog, true);
      window.removeEventListener('resize', this.repositionDialog, true);
      window.removeEventListener('mousedown', this.handleGlobalClick, true);
    }
  };

  handleGlobalClick = (event: MouseEvent) => {
    const input = document.getElementById('passwordInput');
    const dialogPanel = document.querySelector('.password-dialog-panel');
    const target = event.target as HTMLElement;
    if (input && input.contains(target)) return;
    if (dialogPanel && dialogPanel.contains(target)) return;
    this.closePasswordDialog();
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.closePasswordDialog();
    }
  };

  repositionDialog = () => {
    if (!this.dialogRef) return;
    const input = document.getElementById('passwordInput');
    if (!input) return;
    const rect = input.getBoundingClientRect();
    this.dialogRef.updatePosition({ top: `${rect.top}px`, left: `${rect.right + 32}px` });
  };
  recoveryForm: FormGroup;
  loading = false;
  successMsg = '';
  errorMsg = '';
  code: string;

  showPasswordPopover = false;
  passwordReqs = {
    length: null as boolean | null,
    uppercase: null as boolean | null,
    lowercase: null as boolean | null,
    number: null as boolean | null,
    symbol: null as boolean | null,
    repeated2Consecutive: null as boolean | null
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private loginService: LoginService,
    private router: Router,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private bridgeService: NativeBridgeService
  ) {
    this.recoveryForm = this.fb.group(
      {
        code: ['', [Validators.required, Validators.pattern('^[0-9]{6}$')]],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(12),
            Validators.maxLength(50),
            Validators.pattern(this.PASSWORD_REGEXP)
          ]
        ],
        confirmPassword: ['', [Validators.required]]
      },
      { validators: [this.passwordsMatchValidator()] }
    );
  }

  ngOnInit() {
    this.email = this.route.snapshot.queryParamMap.get('email') || '';
    const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!this.email || !emailRegex.test(this.email)) {
      this.router.navigate(['/login']);
      return;
    }

    this.recoveryForm.controls.password.valueChanges.subscribe((value: string) => {
      this.errorMsg = '';
      if (!value) {
        this.passwordReqs = {
          length: null,
          uppercase: null,
          lowercase: null,
          number: null,
          symbol: null,
          repeated2Consecutive: null
        };
      } else {
        this.passwordReqs.length = value.length >= 12;
        this.passwordReqs.uppercase = /[A-Z]/.test(value);
        this.passwordReqs.lowercase = /[a-z]/.test(value);
        this.passwordReqs.number = /[0-9]/.test(value);
        this.passwordReqs.symbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value);
        this.passwordReqs.repeated2Consecutive = !/(.)\1\1/.test(value);
      }
      if (this.dialogRef && this.dialogRef.componentInstance.updateReqs) {
        this.dialogRef.componentInstance.updateReqs({ ...this.passwordReqs });
      }
    });
    this.recoveryForm.controls.confirmPassword.valueChanges.subscribe(() => {
      this.errorMsg = '';
    });
  }

  getReqClass(val: boolean | null): string {
    if (val === null) return 'req-gray';
    return val ? 'req-green' : 'req-red';
  }
  getReqIcon(val: boolean | null): string {
    if (val === null) return 'radio_button_unchecked';
    return val ? 'check_circle' : 'cancel';
  }

  getReqMenuIcon(val: boolean | null): string {
    if (val === null) return 'radio_button_unchecked';
    return val ? 'check_circle' : 'cancel';
  }

  getReqColor(val: boolean | null): string {
    if (val === null) return '#888'; // gray
    return val ? '#43a047' : '#e53935'; // green or red
  }

  passwordsMatchValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const passwordControl = group.get('password');
      const confirmControl = group.get('confirmPassword');

      if (!passwordControl || !confirmControl) {
        return null; // controls not ready yet
      }

      const password = passwordControl.value;
      const confirm = confirmControl.value;

      // If confirm is empty, let the required validator show its own error.
      if (!confirm) {
        // Avoid overriding unrelated errors.
        if (confirmControl.hasError('passwordMismatch')) {
          const { passwordMismatch, ...others } = confirmControl.errors ?? {};
          confirmControl.setErrors(Object.keys(others).length ? others : null);
        }
        return null;
      }

      // Set/clear the mismatch error on the confirm control.
      if (password !== confirm) {
        const existing = confirmControl.errors ?? {};
        if (!existing['passwordMismatch']) {
          confirmControl.setErrors({ ...existing, passwordMismatch: true });
        }
      } else {
        // Remove only passwordMismatch, preserve other errors.
        const { passwordMismatch, ...others } = confirmControl.errors ?? {};
        confirmControl.setErrors(Object.keys(others).length ? others : null);
      }

      // Returning null ensures the group itself has no error; we attached it to the control.
      return null;
    };
  }

  onSubmit(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.recoveryForm.invalid) return;
    this.loading = true;
    const { code, password } = this.recoveryForm.value;
    this.loginService
      .updatePassword({
        codigo: code,
        password,
        email: this.email,
        platform: 9
      })
      .then(() => {
        this.notificationService.showSuccess('Contraseña actualizada correctamente');
        this.successMsg = 'Contraseña actualizada correctamente.';
        this.loading = false;
        setTimeout(() => this.router.navigate(['/']), 2000);
      })
      .catch((err) => {
        this.notificationService.showError(err?.message || 'Error al actualizar la contraseña.');
        this.errorMsg = err?.message || 'Error al actualizar la contraseña.';
        this.loading = false;
      });
  }

  gotoLogin() {
    if (
      this.bridgeService &&
      (this.bridgeService.nativeBridgeIsActive('androidBridge') || this.bridgeService.nativeBridgeIsActive('iOSBridge'))
    ) {
      this.bridgeService.sendMessageToDevice(JSON.stringify({ message_id: deviceMessageId.USER_SIGN_OUT }));
      console.log('Sent userSignOut to bridge');
    } else {
      console.warn('Bridge no disponible, solo navega a login');
    }
    this.router.navigate(['/login']);
  }
}
